<template>
  <v-row class="patient-information-container">
    <v-col v-if="orderInfoPreview" cols="12">
      <v-row class="patient-information">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Requesting Provider or Prescriber</h3>
        </v-col>

        <v-col cols="12" class="text-body-3-1 weight-600 text-primary">PROVIDER INFORMATION</v-col>

        <v-col
          v-if="rosterChecked && rosterStatus === 'unmatched' && providerDetailsComplete"
          cols="12"
          class="provider-address-book-banner">
          <v-alert density="compact" variant="tonal" color="info" icon="mdi-book-plus-outline">
            This provider isn't in your address book yet.
            <v-btn
              size="small"
              variant="tonal"
              color="info"
              class="ml-2"
              :loading="addingToAddressBook"
              @click="addToAddressBook">
              Add to address book
            </v-btn>
            <div v-if="addToAddressBookError" class="text-error text-caption mt-1">
              {{ addToAddressBookError }}
            </div>
          </v-alert>
        </v-col>

        <v-col v-if="props.isEditable && hasProviders" cols="12">
          <RemoteAutocomplete
            ref="providerSearchRef"
            :model-value="selectedProviderKey"
            :remote-search="searchProviders"
            :allow-free-text="false"
            :component-bind="{ noFilter: true, clearable: true, placeholder: 'Start typing provider name or NPI' }"
            class="lh--force-white-background"
            label="Search Provider (Name or NPI)"
            @update:model-value="handleProviderSelection">
            <template #item="{ props: itemProps, item }">
              <v-list-item v-if="item.raw?.isLoadMore" v-bind="{ ...itemProps, onClick: loadMoreProviders }">
                <template #title>
                  <span class="text-primary font-weight-medium">Load more results...</span>
                </template>
              </v-list-item>
              <v-list-item v-else v-bind="itemProps" lines="two">
                <template #title>{{ item.raw.provider.displayName }}</template>
                <template #subtitle>
                  <span class="text-caption">
                    NPI: {{ item.raw.provider.npi || "" }} | {{ item.raw.provider.address1 || "" }},
                    {{ item.raw.provider.city || "" }}, {{ item.raw.provider.state || "" }}
                  </span>
                </template>
              </v-list-item>
            </template>
          </RemoteAutocomplete>
        </v-col>

        <v-col cols="12" sm="6">
          <FeedbackInputWrapper
            :field-id="'providerFirstName'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerFirstName"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerFirstName') && props.formButtonClicked
                ? 'Please enter provider first name.'
                : null
            "
            label="providerFirstName">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerFirstName"
                type="text"
                placeholder="First Name"
                label="First Name*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerFirstName', orderInfoPreview.providerFirstName);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerFirstName"
            :entry="discrepancyFor('providerFirstName')"
            :loading="applyingField === 'providerFirstName'"
            :error="rosterApplyErrorFor('providerFirstName')"
            @apply="applyRosterField" />
        </v-col>
        <v-col cols="12" sm="6">
          <FeedbackInputWrapper
            :field-id="'providerLastName'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerLastName"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerLastName') && props.formButtonClicked
                ? 'Please enter provider last name.'
                : null
            "
            label="providerLastName">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerLastName"
                type="text"
                placeholder="Last Name"
                label="Last Name*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerLastName', orderInfoPreview.providerLastName);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerLastName"
            :entry="discrepancyFor('providerLastName')"
            :loading="applyingField === 'providerLastName'"
            :error="rosterApplyErrorFor('providerLastName')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'providerContactPhone'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerContactPhone"
            :error-messages="
              props.isEditable && !phoneValid('providerContactPhone') && props.formButtonClicked
                ? 'Please enter provider contact phone number.'
                : null
            "
            label="providerContactPhone">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerContactPhone"
                type="text"
                placeholder="Contact Phone"
                label="Contact Phone*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerContactPhone', orderInfoPreview.providerContactPhone);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerContactPhone"
            :entry="discrepancyFor('providerContactPhone')"
            :loading="applyingField === 'providerContactPhone'"
            :error="rosterApplyErrorFor('providerContactPhone')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'providerContactFax'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerContactFax"
            :error-messages="
              props.isEditable && !phoneValid('providerContactFax') && props.formButtonClicked
                ? 'Please enter provider contact fax number.'
                : null
            "
            label="providerContactFax">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerContactFax"
                type="text"
                placeholder="Contact Fax"
                label="Contact Fax*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerContactFax', orderInfoPreview.providerContactFax);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerContactFax"
            :entry="discrepancyFor('providerContactFax')"
            :loading="applyingField === 'providerContactFax'"
            :error="rosterApplyErrorFor('providerContactFax')"
            @apply="applyRosterField" />
          <RegisteredContactDiscrepancy
            v-if="registeredPreference === 'fax'"
            :account-fax="registeredAccount?.fax"
            :form-fax="orderInfoPreview.providerContactFax"
            @apply="applyRegisteredFax" />
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'providerNpiNumber'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerNpiNumber"
            :error-messages="
              props.isEditable && !npiValid('providerNpiNumber') && props.formButtonClicked
                ? 'Please enter a valid provider NPI number.'
                : null
            "
            label="providerNpiNumber">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerNpiNumber"
                type="text"
                placeholder="NPI Number"
                label="NPI Number*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerNpiNumber', orderInfoPreview.providerNpiNumber);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12" class="mb-4">
          <v-divider />
        </v-col>

        <v-col cols="12" class="text-body-3-1 weight-600 text-primary">REQUESTING PROVIDER OR PRESCRIBER ADDRESS</v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'providerAddress1'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerAddress1"
            :error-messages="
              props.isEditable && !addressValid('providerAddress1') && props.formButtonClicked
                ? 'Please enter provider address. If it is over 35 characters, please use address line 2.'
                : null
            "
            label="providerAddress1">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerAddress1"
                type="text"
                placeholder="Address Line 1"
                label="Address Line 1*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerAddress1', orderInfoPreview.providerAddress1);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerAddress1"
            :entry="discrepancyFor('providerAddress1')"
            :loading="applyingField === 'providerAddress1'"
            :error="rosterApplyErrorFor('providerAddress1')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'providerAddress2'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerAddress2"
            label="providerAddress2">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.providerAddress2"
                type="text"
                placeholder="Address Line 2"
                label="Address Line 2"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerAddress2', orderInfoPreview.providerAddress2);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerAddress2"
            :entry="discrepancyFor('providerAddress2')"
            :loading="applyingField === 'providerAddress2'"
            :error="rosterApplyErrorFor('providerAddress2')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12" sm="4">
          <FeedbackInputWrapper
            :field-id="'providerCity'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerCity"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerCity') && props.formButtonClicked
                ? 'Please enter provider city.'
                : null
            "
            label="providerCity">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerCity"
                type="text"
                placeholder="City"
                label="City*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerCity', orderInfoPreview.providerCity);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerCity"
            :entry="discrepancyFor('providerCity')"
            :loading="applyingField === 'providerCity'"
            :error="rosterApplyErrorFor('providerCity')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12" sm="4">
          <FeedbackInputWrapper
            :field-id="'providerState'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerState"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerState') && props.formButtonClicked
                ? 'Please enter provider state.'
                : null
            "
            label="providerState">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-select
                v-model="orderInfoPreview.providerState"
                :items="states"
                placeholder="Select State"
                label="Provider State*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerState', orderInfoPreview.providerState);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerState"
            :entry="discrepancyFor('providerState')"
            :loading="applyingField === 'providerState'"
            :error="rosterApplyErrorFor('providerState')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12" sm="4">
          <FeedbackInputWrapper
            :field-id="'providerZip'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerZip"
            :error-messages="
              props.isEditable && !zipCodeValid('providerZip') && props.formButtonClicked
                ? 'Please enter provider ZIP.'
                : null
            "
            label="providerZip">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerZip"
                type="text"
                placeholder="ZIP"
                label="ZIP*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerZip', orderInfoPreview.providerZip);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
          <ProviderFieldDiscrepancy
            field="providerZip"
            :entry="discrepancyFor('providerZip')"
            :loading="applyingField === 'providerZip'"
            :error="rosterApplyErrorFor('providerZip')"
            @apply="applyRosterField" />
        </v-col>

        <v-col cols="12">
          <ProviderDefaultCommunicationMethod
            ref="providerDefaultCommunicationMethodRef"
            :enabled="forwardResponsesToProviderByEmail"
            :email="orderInfoPreview.providerEmail"
            :readonly="!props.isEditable"
            :disabled="!props.isEditable"
            :show-errors="props.formButtonClicked"
            :registered-preference="registeredPreference"
            @update:enabled="onForwardResponsesEnabledChange"
            @update:email="onProviderEmailChange" />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { decamelize } from "humps";
import { usaStates } from "@/utils/usaStates";
import { isNPIValid, isPhoneNumberValid, isZipCodeValid } from "@/utils/validators";
import { tryParseApiErrors } from "@/utils";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";
import ProviderDefaultCommunicationMethod from "@/components/ProviderDefaultCommunicationMethod.vue";
import { useAuthStore } from "@/stores";
import ProviderFieldDiscrepancy from "@/components/ProviderFieldDiscrepancy.vue";
import RegisteredContactDiscrepancy from "@/components/RegisteredContactDiscrepancy.vue";
import { useRegisteredProviderAccount } from "@/composables/useRegisteredProviderAccount";
import { mainServices } from "@/services/mainServices";
import debounce from "lodash.debounce";

const states = ref([]);
onMounted(async () => {
  for (let stateData of Object.values(usaStates)) {
    states.value.push(stateData.name);
  }
  // Check if client company has any providers in the database
  await checkIfProvidersExist();
});

const convertStateToFullName = (stateValue) => {
  if (!stateValue) return "";
  const trimmed = stateValue.trim();

  // Check if it's already a full state name
  if (states.value.includes(trimmed)) {
    return trimmed;
  }

  // Try to find by abbreviation
  const upperAbbrev = trimmed.toUpperCase();
  for (let stateData of Object.values(usaStates)) {
    if (stateData.abbreviation === upperAbbrev) {
      return stateData.name;
    }
  }

  // Return original if no match found
  return trimmed;
};

const emit = defineEmits(["updateOrderInfoField", "clearProviderSuccess"]);

const props = defineProps({
  orderInfo: {
    type: Object,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const orderInfoPreview = ref(props.orderInfo);

watch(
  () => props.orderInfo,
  (value) => {
    orderInfoPreview.value = value;
  }
);

const providerDefaultCommunicationMethodRef = ref(null);

const forwardResponsesToProviderByEmail = computed(() =>
  Boolean(orderInfoPreview.value.forwardResponsesToProviderByEmail)
);

function onForwardResponsesEnabledChange(enabled) {
  emit("updateOrderInfoField", "forwardResponsesToProviderByEmail", enabled);
  if (!enabled) {
    onProviderEmailChange(null);
  }
}

function onProviderEmailChange(email) {
  emit("updateOrderInfoField", "providerEmail", email || null);
}

function validProviderDefaultCommunicationMethod() {
  return providerDefaultCommunicationMethodRef.value?.validate() ?? true;
}

defineExpose({ validProviderDefaultCommunicationMethod });

// Registered provider login (LAM-5508). A prescriber with a Do Prior Auth login chose how tenants reach
// them, and LAM-5263 already honours that choice — and their confirmed contact — over these fields at
// send time. So the method here follows their account (locked and flagged). For a fax preference the
// account's confirmed fax fills an empty fax field and a differing one is flagged beside it, never
// overwritten; for email the tenant still types the fallback address — the login email a send actually
// uses is account-held and never leaves the server. Skipped on read-only forms and for provider logins.
const { account: registeredAccount } = useRegisteredProviderAccount(
  () => orderInfoPreview.value.providerNpiNumber,
  () => props.isEditable && !useAuthStore().userInfo?.isProvider
);
const registeredPreference = computed(() => registeredAccount.value?.communicationPreference ?? null);

// Writes follow the user, not the page. Only an NPI entered after the form opened changes the order, and
// only while the form is editable: the NPI it opened with is shown locked and flagged but never written,
// so a page load cannot arm "Re-Open Submission" through the change tracker, and losing the edit lock
// (which switches the lookup off) cannot rewrite a record the user no longer holds. What the account wrote
// is remembered per NPI and undone once the NPI moves on — in reverse, only where the form still holds
// exactly that value.
const openedWithNpi = String(orderInfoPreview.value.providerNpiNumber ?? "").replace(/\D/g, "");
let applied = null;

// Same rule as the user's own toggle (onForwardResponsesEnabledChange): forwarding off drops the email.
function setForwardByEmail(byEmail) {
  emit("updateOrderInfoField", "forwardResponsesToProviderByEmail", byEmail);
  if (!byEmail) emit("updateOrderInfoField", "providerEmail", null);
}

function undoApplied() {
  for (const { field, from, to } of [...applied.ops].reverse()) {
    if (orderInfoPreview.value[field] !== to) continue;
    if (field === "forwardResponsesToProviderByEmail") setForwardByEmail(Boolean(from));
    else emit("updateOrderInfoField", field, from);
  }
  applied = null;
}

watch(registeredAccount, (account) => {
  if (!props.isEditable) return;
  const npi = npiDigits(orderInfoPreview.value.providerNpiNumber);
  if (applied && applied.npi !== npi) undoApplied();
  const preference = account?.communicationPreference;
  if (!preference || npi === openedWithNpi || applied?.npi === npi) return;
  const ops = [];
  const record = (field, to) => ops.push({ field, from: orderInfoPreview.value[field] ?? null, to });
  const byEmail = preference === "email";
  if (Boolean(orderInfoPreview.value.forwardResponsesToProviderByEmail) !== byEmail) {
    record("forwardResponsesToProviderByEmail", byEmail);
    if (!byEmail && orderInfoPreview.value.providerEmail) record("providerEmail", null);
    setForwardByEmail(byEmail);
  }
  if (!byEmail && account.fax && !String(orderInfoPreview.value.providerContactFax ?? "").trim()) {
    record("providerContactFax", account.fax);
    emit("updateOrderInfoField", "providerContactFax", account.fax);
  }
  applied = { npi, ops };
});

function applyRegisteredFax(fax) {
  emit("updateOrderInfoField", "providerContactFax", fax);
}

// Form-vs-address-book provider reconciliation (LAM-4826). Keys are camelCase provider field names.
// The state lives here rather than on the order: the address book is tenant-scoped and reachable
// with no order at all, so Create Submission gets the same banner and chips as a saved order's form.
// Chips stay empty unless the NPI resolves to a confirmed, submission-owned (not roster-owned)
// ClientCompanyProvider — a roster-imported row is protected and never offered for edit.
const PROVIDER_ADDRESS_BOOK_FIELDS = [
  "providerFirstName",
  "providerLastName",
  "providerTitle",
  "providerOfficeName",
  "providerNpiNumber",
  "providerContactPhone",
  "providerContactFax",
  "providerAddress1",
  "providerAddress2",
  "providerCity",
  "providerState",
  "providerZip",
];

const rosterStatus = ref("unmatched");
const rosterDiscrepancies = ref({});
const rosterChecked = ref(false);
const applyingField = ref(null);
const rosterApplyErrors = ref({});
const addingToAddressBook = ref(false);
const addToAddressBookError = ref(null);
const discrepancyFor = (field) => rosterDiscrepancies.value[field] || null;
const rosterApplyErrorFor = (field) => rosterApplyErrors.value[field] || null;

// The address-book row the user picked out of the search, kept as {id, npi}. One NPI can map to
// several rows (same prescriber, several practice addresses), so this is the only thing that says
// which one they meant — without it the server has to guess and gives up, reporting the provider as
// missing from the book they just picked them out of.
const selectedRosterProvider = ref(null);
const npiDigits = (value) => String(value || "").replace(/\D/g, "");

// Drops the remembered row once the NPI no longer matches it: at that point the user has moved on
// to a different provider and the id would point at the wrong one.
const selectedRosterProviderId = computed(() => {
  const selected = selectedRosterProvider.value;
  if (!selected || npiDigits(selected.npi) !== npiDigits(orderInfoPreview.value.providerNpiNumber)) return null;
  return selected.id;
});

const providerAddressBookPayload = () => ({
  ...Object.fromEntries(PROVIDER_ADDRESS_BOOK_FIELDS.map((field) => [field, orderInfoPreview.value[field] || null])),
  providerId: selectedRosterProviderId.value,
});

// Guards the "unmatched" banner from showing on a form the user hasn't finished filling in — the
// address book matches on NPI alone, and no row can be created without a name either.
const hasEnoughProviderInfoForAddressBook = computed(() => {
  const preview = orderInfoPreview.value;
  return Boolean(
    preview.providerFirstName?.trim() && preview.providerLastName?.trim() && preview.providerNpiNumber?.trim()
  );
});

// The "add to address book" offer waits for the whole required provider block (the set
// validProviderInfo checks), not just name + NPI, which is all the lookup itself needs. Offered on
// name + NPI it fires while the user is still typing the address, and a row added that early then
// greets every later field with its own "save" chip. The lookup keeps running on name + NPI so a
// matched provider's chips still appear as soon as they apply.
const PROVIDER_ADD_REQUIRED_FIELDS = [
  "providerFirstName",
  "providerLastName",
  "providerNpiNumber",
  "providerContactPhone",
  "providerContactFax",
  "providerAddress1",
  "providerCity",
  "providerState",
  "providerZip",
];
const providerDetailsComplete = computed(
  () =>
    hasEnoughProviderInfoForAddressBook.value &&
    PROVIDER_ADD_REQUIRED_FIELDS.every((field) => String(orderInfoPreview.value[field] ?? "").trim() !== "")
);

// Debouncing only suppresses lookups that haven't fired yet — one already in flight still resolves,
// and would repaint the banner and chips from a form state the user has since moved on from. Every
// lookup takes a ticket, and anything newer voids the ones before it: a later lookup, a completed
// write, or simply the form changing (see the watcher below).
let addressBookLookupSeq = 0;
const supersedeAddressBookLookups = () => (addressBookLookupSeq += 1);

function applyAddressBookState(result) {
  supersedeAddressBookLookups();
  rosterStatus.value = result?.status || "unmatched";
  rosterDiscrepancies.value = result?.discrepancies || {};
  rosterChecked.value = true;
}

// Ask the server where this provider stands. Only worth a call once the NPI is a plausible match
// key — a malformed one can never resolve a row, so the answer is already known.
async function refreshAddressBookState() {
  const lookupId = supersedeAddressBookLookups();
  // Only a change to the provider fields gets us here, so anything an apply or an add failed with
  // was about a form state — often a different provider entirely — that is no longer on screen.
  rosterApplyErrors.value = {};
  addToAddressBookError.value = null;
  // The address book is tenant-scoped; a provider login (LAM-5263) has no company and 403s on it.
  if (
    useAuthStore().userInfo?.isProvider ||
    !hasEnoughProviderInfoForAddressBook.value ||
    !isNPIValid(orderInfoPreview.value.providerNpiNumber || "")
  ) {
    rosterStatus.value = "unmatched";
    rosterDiscrepancies.value = {};
    rosterChecked.value = false;
    return;
  }
  try {
    const result = await mainServices.reconcileProviderAddressBook(providerAddressBookPayload());
    if (lookupId !== addressBookLookupSeq) return;
    applyAddressBookState(result);
  } catch (err) {
    if (lookupId !== addressBookLookupSeq) return;
    // A lookup that failed proves nothing about the address book, so it must not leave the UI
    // telling the user this provider is missing from it.
    rosterChecked.value = false;
  }
}

const debouncedRefreshAddressBookState = debounce(refreshAddressBookState, 600);

// Every provider field feeds the comparison, not just the NPI: editing a phone number changes which
// chips apply even though the matched row itself doesn't change.
watch(
  providerAddressBookPayload,
  () => {
    // Void in-flight lookups here rather than leaving it to the replacement lookup: that one is
    // debounced, so until it fires the answer already on the wire still holds the newest ticket and
    // would repaint the banner and chips over a form that has moved on to another provider.
    supersedeAddressBookLookups();
    debouncedRefreshAddressBookState();
  },
  { immediate: true }
);

async function applyRosterField(field) {
  const entry = discrepancyFor(field);
  if (!entry || applyingField.value) return;
  applyingField.value = field;
  rosterApplyErrors.value = { ...rosterApplyErrors.value, [field]: null };
  try {
    // The backend matches this against the submission form's (snake_case) field names — humps only
    // decamelizes object keys in the request body, never string values, so this must be converted
    // explicitly rather than relying on the axios interceptor.
    applyAddressBookState(
      await mainServices.applyProviderAddressBookField(providerAddressBookPayload(), decamelize(field))
    );
  } catch (err) {
    // A 400 here is expected by design: the server re-verifies against the roster and rejects if it
    // changed (or the roster row's source changed) between render and click. Surface it instead of
    // silently leaving the chip in place with no feedback.
    rosterApplyErrors.value = { ...rosterApplyErrors.value, [field]: tryParseApiErrors(err).join(" ") };
  } finally {
    applyingField.value = null;
  }
}

async function addToAddressBook() {
  if (addingToAddressBook.value) return;
  addingToAddressBook.value = true;
  addToAddressBookError.value = null;
  try {
    applyAddressBookState(await mainServices.addProviderToAddressBook(providerAddressBookPayload()));
  } catch (err) {
    addToAddressBookError.value = tryParseApiErrors(err).join(" ");
  } finally {
    addingToAddressBook.value = false;
  }
}

// Provider search
const LOAD_MORE_VALUE = "__load_more__";
const providerSearchRef = ref(null);
const selectedProviderKey = ref(null);
const providerSearchNextPage = ref(null);
const hasProviders = ref(false);

// Check if the client company has any providers in the database
// Uses count endpoint to determine if provider search should be shown
const checkIfProvidersExist = async () => {
  try {
    const response = await mainServices.getProvidersCount();
    hasProviders.value = (response.count || 0) > 0;
  } catch (error) {
    // If check fails, default to hiding the search bar to be safe
    hasProviders.value = false;
  }
};

// Item value doubles as the field tooltip, so it reads as name + NPI rather than a row id; the full row travels in `provider`
const toProviderItem = (provider) => {
  const displayName = `${provider.firstName || ""} ${provider.lastName || ""}`.trim();
  return {
    title: displayName,
    value: `${displayName} (NPI ${provider.npi || ""})`,
    provider: { ...provider, displayName },
  };
};

const loadMoreItem = { title: "Load more results...", value: LOAD_MORE_VALUE, isLoadMore: true };

// Fetch one page and remember whether another one follows
const fetchProviderPage = async (query, page) => {
  const results = await mainServices.searchProviders(query.trim(), page);
  providerSearchNextPage.value = results.next ? page + 1 : null;
  return [...results.results.map(toProviderItem), ...(results.next ? [loadMoreItem] : [])];
};

// Search providers with client company filtering
// Note: The backend automatically filters providers by the authenticated user's client company
// via client_company_filter, ensuring only applicable providers are returned
const searchProviders = (query) => {
  if (!query || query.trim().length === 0) {
    providerSearchNextPage.value = null;
    return Promise.resolve([]);
  }
  return fetchProviderPage(query, 1).catch(() => {
    providerSearchNextPage.value = null;
    return [];
  });
};

const loadMoreProviders = async () => {
  const remote = providerSearchRef.value?.remote;
  if (!providerSearchNextPage.value || !remote?.searchRef) {
    return;
  }
  try {
    const nextItems = await fetchProviderPage(remote.searchRef, providerSearchNextPage.value);
    remote.itemsRef = [...remote.itemsRef.filter((item) => !item.isLoadMore), ...nextItems];
  } catch (error) {
    providerSearchNextPage.value = null;
  }
};

const handleProviderSelection = (value) => {
  if (!value || value === LOAD_MORE_VALUE) {
    selectedProviderKey.value = null;
    return;
  }
  const item = providerSearchRef.value?.remote.itemsRef.find((i) => i.value === value);
  if (item) {
    selectedProviderKey.value = value;
    const provider = item.provider;
    selectedRosterProvider.value = { id: provider.id, npi: provider.npi };
    orderInfoPreview.value.providerFirstName = provider.firstName || "";
    orderInfoPreview.value.providerLastName = provider.lastName || "";
    orderInfoPreview.value.providerNpiNumber = provider.npi || "";
    // Neither of these is rendered on this form, so nothing else would ever refresh them — left
    // alone they keep the previously selected (or intake-extracted) provider's values and get
    // written onto this provider's address-book row.
    orderInfoPreview.value.providerTitle = provider.title || "";
    orderInfoPreview.value.providerOfficeName = provider.organization || "";
    orderInfoPreview.value.providerContactPhone = provider.contactPhone || "";
    orderInfoPreview.value.providerContactFax = provider.contactFax || "";
    orderInfoPreview.value.providerAddress1 = provider.address1 || "";
    orderInfoPreview.value.providerAddress2 = provider.address2 || "";
    orderInfoPreview.value.providerCity = provider.city || "";
    orderInfoPreview.value.providerState = convertStateToFullName(provider.state || "");
    orderInfoPreview.value.providerZip = provider.zip || "";

    emit("updateOrderInfoField", "providerFirstName", orderInfoPreview.value.providerFirstName);
    emit("updateOrderInfoField", "providerLastName", orderInfoPreview.value.providerLastName);
    emit("updateOrderInfoField", "providerNpiNumber", orderInfoPreview.value.providerNpiNumber);
    emit("updateOrderInfoField", "providerTitle", orderInfoPreview.value.providerTitle);
    emit("updateOrderInfoField", "providerOfficeName", orderInfoPreview.value.providerOfficeName);
    emit("updateOrderInfoField", "providerContactPhone", orderInfoPreview.value.providerContactPhone);
    emit("updateOrderInfoField", "providerContactFax", orderInfoPreview.value.providerContactFax);
    emit("updateOrderInfoField", "providerAddress1", orderInfoPreview.value.providerAddress1);
    emit("updateOrderInfoField", "providerAddress2", orderInfoPreview.value.providerAddress2);
    emit("updateOrderInfoField", "providerCity", orderInfoPreview.value.providerCity);
    emit("updateOrderInfoField", "providerState", orderInfoPreview.value.providerState);
    emit("updateOrderInfoField", "providerZip", orderInfoPreview.value.providerZip);
  }
};

// Validations
function fieldNotEmpty(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "";
}

function addressValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && value.length <= 35;
}

function npiValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isNPIValid(value);
}

function phoneValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isPhoneNumberValid(value);
}

function zipCodeValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isZipCodeValid(value);
}

// Watch for field changes to clear success message when user edits
// Clear success state when provider fields change to indicate user is making edits
watch(
  () => [
    orderInfoPreview.value.providerFirstName,
    orderInfoPreview.value.providerLastName,
    orderInfoPreview.value.providerNpiNumber,
  ],
  () => {
    emit("clearProviderSuccess");
  },
  { immediate: false }
);
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
